/**
 * Cliente HTTP compartido. **Espejo de `back/src/**`**: ante divergencia,
 * manda el back (§2.1 del blueprint).
 *
 * Los tipos de abajo replican los DTO de
 * `back/src/asistencia/core/contratos/index.ts`. Si cambia un contrato allá,
 * cambia acá; no se inventan campos que el back no expone.
 *
 * El front habla **solo** con esta API: nunca con Visma, GeoVictoria ni los
 * TMS, y jamás ve credenciales ni tokens.
 */

/** Error de la API con el detalle que el back agrega a los bloqueos. */
export class ErrorApi extends Error {
  readonly status: number;
  readonly codigo: string | undefined;
  readonly referencia: string | undefined;
  readonly responsable: string | undefined;

  constructor(
    mensaje: string,
    status: number,
    extra: { codigo?: string; referencia?: string; responsable?: string } = {},
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
    this.status = status;
    this.codigo = extra.codigo;
    this.referencia = extra.referencia;
    this.responsable = extra.responsable;
  }

  /** Texto para mostrar: los bloqueos abiertos dicen quién los destraba. */
  get mensajeLegible(): string {
    if (this.codigo === 'BLOQUEO_ABIERTO') {
      return `${this.message} (bloqueo ${this.referencia ?? ''} — responsable: ${
        this.responsable ?? 'sin asignar'
      })`;
    }
    return this.message;
  }
}

interface CuerpoError {
  error?: string;
  codigo?: string;
  referencia?: string;
  responsable?: string;
}

/**
 * `fetch` con `credentials: same-origin` (la sesión va por cookie firmada) y
 * el error de la API ya convertido en `ErrorApi`.
 */
/**
 * Error de una consulta que el usuario canceló.
 *
 * Se distingue del resto porque no es una falla: la pantalla lo informa como
 * un aviso neutro y no en rojo. `fetch` avisa de un abort con un `DOMException`
 * llamado `AbortError`, que no dice nada legible.
 */
export class Cancelada extends Error {
  constructor() {
    super('La consulta se canceló.');
    this.name = 'Cancelada';
  }
}

export const fueCancelada = (err: unknown): boolean =>
  err instanceof Cancelada ||
  (err instanceof DOMException && err.name === 'AbortError') ||
  (err instanceof Error && err.name === 'AbortError');

export async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  let respuesta: Response;
  try {
    respuesta = await fetch(ruta, {
      credentials: 'same-origin',
      ...opciones,
      headers: {
        // Solo para cuerpos JSON. Con `FormData` hay que dejar que el
        // navegador ponga el Content-Type, porque tiene que incluir el
        // `boundary`.
        ...(typeof opciones.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
        ...opciones.headers,
      },
    });
  } catch (err) {
    if (fueCancelada(err)) throw new Cancelada();
    throw err;
  }

  const texto = await respuesta.text();

  /*
   * La API contesta JSON siempre. Si llega HTML es que la respuesta no vino
   * del back: en producción, Apache sirviendo su página de error porque Node
   * no está escuchando en el puerto al que apunta el proxy, o el
   * `FallbackResource` devolviendo el index.html porque el proxy de `/api`
   * no quedó aplicado.
   *
   * Se distingue a propósito de un error de la API, porque se arregla en otro
   * lado. `JSON.parse` a secas decía «Unexpected token '<'», y ese texto
   * terminaba mostrándose como el motivo por el que la pestaña no estaría
   * habilitada: dos mensajes engañosos para un problema que no es del módulo.
   */
  let datos: unknown = null;
  if (texto) {
    try {
      datos = JSON.parse(texto);
    } catch {
      throw new ErrorApi(
        `La API no respondió (HTTP ${respuesta.status}): devolvió ` +
          `${respuesta.headers.get('content-type') ?? 'contenido desconocido'} en lugar de JSON. ` +
          'Revisar que el back esté corriendo y que el proxy de /api apunte a su puerto.',
        respuesta.status,
        { codigo: 'RESPUESTA_NO_JSON' },
      );
    }
  }

  if (!respuesta.ok) {
    const cuerpo = (datos ?? {}) as CuerpoError;
    throw new ErrorApi(cuerpo.error ?? `Error ${respuesta.status}`, respuesta.status, {
      ...(cuerpo.codigo !== undefined ? { codigo: cuerpo.codigo } : {}),
      ...(cuerpo.referencia !== undefined ? { referencia: cuerpo.referencia } : {}),
      ...(cuerpo.responsable !== undefined ? { responsable: cuerpo.responsable } : {}),
    });
  }
  return datos as T;
}

/** POST con cuerpo JSON, que es el 90% de las escrituras del portal. */
export const postear = <T>(ruta: string, cuerpo?: unknown): Promise<T> =>
  pedir<T>(ruta, {
    method: 'POST',
    ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
  });

/** PATCH: solo los campos que se tocaron. */
export const parchear = <T>(ruta: string, cuerpo: unknown): Promise<T> =>
  pedir<T>(ruta, { method: 'PATCH', body: JSON.stringify(cuerpo) });

/** PUT para las pantallas de configuración, que reemplazan el recurso entero. */
export const poner = <T>(ruta: string, cuerpo: unknown): Promise<T> =>
  pedir<T>(ruta, { method: 'PUT', body: JSON.stringify(cuerpo) });

export const mensajeDe = (err: unknown): string =>
  err instanceof ErrorApi ? err.mensajeLegible : err instanceof Error ? err.message : String(err);

// ===========================================================================
// Tipos espejo — asistencia (core/contratos/index.ts)
// ===========================================================================

export type EstadoJornada =
  | 'ASISTENCIA_AL_VIAJE'
  | 'SIN_TAREA'
  | 'ESPERANDO_VIAJE'
  | 'IMPACTADA'
  | 'SIN_TURNO_CONFIGURADO'
  | 'LICENCIA_A_CORREGIR'
  | 'VIAJE_SIN_FICHADA'
  | 'RESUELTA_POR_SUPERVISOR';

export type TipoImpacto =
  | 'SALIDA_ARTIFICIAL'
  | 'LICENCIA_ASISTENCIA_VIAJE'
  | 'LICENCIA_SIN_TAREA';

export type ResultadoImpacto = 'OK' | 'RECHAZADO' | 'ERROR';
export type DecisionSupervisor = 'CONFIRMA_ASISTENCIA' | 'DESCARTA' | 'DERIVA';
export type Modo = 'real' | 'demo';
export type RolPortal = 'rrhh' | 'liquidador' | 'admin';

/** Cómo se llama cada rol en la pantalla. El código usa el identificador. */
export const ETIQUETA_ROL: Readonly<Record<RolPortal, string>> = {
  admin: 'Administrador',
  liquidador: 'Liquidador',
  rrhh: 'Personal RRHH',
};

export interface FichadaDto {
  readonly fechaHora: string;
  readonly hora: string;
  readonly tipo: 'ENTRADA' | 'SALIDA';
  readonly origen: 'RELOJ' | 'APP' | 'ARTIFICIAL';
}

export type FuncionEnViaje = 'CHOFER' | 'ACOMPANANTE';

export interface ViajeDto {
  readonly identificadorViaje: string;
  readonly descripcion: string | null;
  /** Rol en el viaje. Los acompañantes se procesan igual que los choferes. */
  readonly funcion: FuncionEnViaje;
  /** Inicio y fin del viaje con hora, 'AAAA-MM-DD HH:mm'. */
  readonly inicio: string | null;
  readonly fin: string | null;
}

export interface TurnoDto {
  readonly horaInicio: string;
  readonly horaFin: string;
  readonly cruzaMedianoche: boolean;
  readonly origen: 'GEOVICTORIA' | 'TABLA_PORTAL';
}

export interface ImpactoDto {
  readonly id: string;
  readonly tipoImpacto: TipoImpacto;
  readonly referenceIdentifier: string;
  readonly resultado: ResultadoImpacto;
  readonly usuario: string;
  readonly fechaHora: string;
  readonly detalle: string | null;
  readonly payloadEnviado: unknown;
  readonly respuestaGv: unknown;
}

export interface ResolucionDto {
  readonly decision: DecisionSupervisor;
  readonly comentario: string;
  readonly usuario: string;
  readonly fechaHora: string;
}

export interface JornadaDto {
  readonly dni: string;
  readonly nombre: string | null;
  readonly grupo: string | null;
  /** Operación dueña de los datos: CODYLSA, DYLASA, TDU o MALARGUE. */
  readonly empresa: string | null;
  readonly fechaOperativa: string;
  readonly estado: EstadoJornada;
  readonly etiquetaEstado: string;
  readonly motivo: string;
  readonly fichadaEntrada: FichadaDto | null;
  readonly fichadaSalida: FichadaDto | null;
  readonly viaje: ViajeDto | null;
  readonly turno: TurnoDto | null;
  readonly salidaCalculada: string | null;
  readonly horaSalidaCalculada: string | null;
  /** Día de la salida, ya en la zona del portal (lo calcula el servidor). */
  readonly fechaSalidaCalculada: string | null;
  readonly admiteImpacto: boolean;
  /** Qué se va a registrar en GeoVictoria si se impacta esta jornada. */
  readonly accionesRequeridas: readonly TipoImpacto[];
  readonly etiquetasAcciones: readonly string[];
  readonly impactos: readonly ImpactoDto[];
  readonly resolucion: ResolucionDto | null;
}

export interface ListadoJornadas {
  readonly jornadas: readonly JornadaDto[];
  readonly total: number;
  readonly pagina: number;
  readonly tamanio: number;
  readonly modo: Modo;
  readonly avisos: readonly string[];
  /** Empresas presentes en el rango, para el desplegable del filtro. */
  readonly empresas: readonly string[];
}

export interface ResultadoImpactoDto {
  readonly dni: string;
  readonly fecha: string;
  readonly tipoImpacto: TipoImpacto;
  readonly resultado: ResultadoImpacto;
  readonly yaEstaba: boolean;
  readonly detalle: string | null;
}

export interface ResultadoMasivo {
  readonly impactados: readonly ResultadoImpactoDto[];
  readonly rechazados: readonly ResultadoImpactoDto[];
  readonly yaImpactados: readonly ResultadoImpactoDto[];
  /** Las jornadas tocadas, ya reevaluadas: se actualizan esas filas y nada más. */
  readonly jornadas: readonly JornadaDto[];
}

export interface Sesion {
  readonly usuario: string;
  readonly rol: RolPortal;
  readonly unidades: readonly string[];
  readonly nombre?: string | null;
  /** Clave provisoria: hay que cambiarla antes de poder operar. */
  readonly debeCambiarClave?: boolean;
}

/** Usuario del portal, como lo devuelve `/api/asistencia/usuarios`. */
export interface UsuarioDto {
  readonly usuario: string;
  readonly nombre: string;
  readonly rol: RolPortal;
  readonly activo: boolean;
  readonly debeCambiarClave: boolean;
  readonly ultimoIngreso: string | null;
  readonly creadoPor: string;
  readonly creadoEn: string | null;
  readonly actualizadoPor: string;
  readonly actualizadoEn: string | null;
}

export interface EstadoModulo {
  readonly habilitado: boolean;
  readonly motivo?: string;
  readonly faltantes?: readonly string[];
  readonly modo?: Modo;
  /** Mínimo de caracteres de una clave. Lo define el back. */
  readonly largoMinimoClave?: number;
  /** Zona operativa (`PORTAL_TZ`): en ella se muestran las marcas de tiempo. */
  readonly zona?: string;
  /** Etapa demo sin validación de usuario: no hay pantalla de ingreso. */
  readonly sinLogin?: boolean;
  /** Solo lectura: se consulta todo, no se registra nada en GeoVictoria. */
  readonly soloLectura?: boolean;
  readonly bloqueos?: readonly string[];
  readonly licenciasDisponibles?: boolean;
}

/** Una operación del histórico: impacto o resolución, ya unificadas. */
export interface OperacionHistorica {
  readonly clase: 'IMPACTO' | 'RESOLUCION';
  readonly fechaHora: string;
  readonly dni: string;
  readonly fechaOperativa: string;
  /** Tipo de impacto o decisión del supervisor. */
  readonly operacion: string;
  /** Las resoluciones no tienen resultado: vienen en null. */
  readonly resultado: ResultadoImpacto | null;
  readonly usuario: string;
  readonly detalle: string | null;
  readonly referencia: string | null;
}

// ===========================================================================
// Tipos espejo — novedades (back/src/novedades/tipos.ts)
// ===========================================================================

/**
 * Un tramo de viaje del TMS, ya normalizado por el back.
 *
 * Es el grano de la consulta: una fila por colaborador y tramo. La identidad
 * en esta pestaña es el **DNI**; el legajo viaja al lado como dato.
 */
export interface TramoTms {
  readonly operacion: string;
  readonly funcion: string;
  readonly legajo: string;
  readonly tipoChofer: string;
  readonly nombreChofer: string;
  readonly dni: string;
  readonly cuit: string;
  readonly vehiculo: string;
  readonly nroViaje: string;
  readonly tramo: number;
  /** Id del tramo: dos filas con el mismo id son el mismo tramo visto desde
   *  dos documentos de carga. */
  readonly idTramo: string;
  /** Id de la asignación colaborador-tramo, grano de los adicionales. */
  readonly idChoferTramo: string;
  readonly empresa: string;
  /** Código de servicio: las reglas filtran por este, no por la descripción. */
  readonly codigoServicio: string;
  readonly estadoTramo: string;
  readonly fechaInicio: string | null;
  readonly fechaFin: string | null;
  readonly servicio: string;
  readonly destino: string;
  readonly kmEstimado: number;
  readonly kmTotal: number;
  readonly docCliente: string;
  readonly cuenta: string;
  readonly sub: string;
  readonly razonSocial: string;
  readonly horasNormal: number;
  readonly horas50: number;
  readonly horas100: number;
  readonly bultos: number;
  readonly bultosEntregados: number;
  readonly pallets: number;
  readonly palletsEntregados: number;
  readonly kilos: number;
  readonly kilosEntregados: number;
  readonly mt3: number;
  readonly mt3Entregados: number;
  readonly cantAdicionales: number;
  /** Códigos de concepto adicional del tramo, sin su descripción. */
  readonly adicionales: readonly string[];
}

export interface RespuestaOperaciones {
  readonly operaciones: readonly string[];
}

/** Lo que le corresponde a un colaborador en un concepto durante el período. */
export interface NovedadConsolidada {
  readonly dni: string;
  readonly nombre: string;
  readonly legajo: string;
  /**
   * "Propio" o "Eventual". Con las dos poblaciones en la misma lista, es lo
   * que dice a quién se le está liquidando qué.
   */
  readonly tipoChofer: string;
  /** Concepto de Visma, tal como lo define el archivo de RRHH. */
  readonly concepto: string;
  readonly valor: number;
  /** En qué se mide el valor ("h", "km", "u"…), o `''` si no hay una sola. */
  readonly unidad: string;
  readonly fechaDesde: string | null;
  readonly fechaHasta: string | null;
}

/** Un concepto con datos en el período: es lo que se ofrece para impactar. */
export interface ResumenConcepto {
  readonly concepto: string;
  readonly descripcion: string;
  readonly colaboradores: number;
  readonly total: number;
  /** En qué se mide el total, o `''` si el concepto junta reglas distintas. */
  readonly unidad: string;
  /**
   * El concepto no se liquida por el mes: se consulta por un intervalo de
   * fechas que escribe quien liquida. Mientras no se consulte viene en cero.
   */
  readonly rangoPropio: boolean;
  /** Con qué intervalo se consultó, o `null` si todavía no se consultó. */
  readonly rango: RangoDeFechas | null;
}

/** Un intervalo de días, "AAAA-MM-DD"; los dos extremos entran. */
export interface RangoDeFechas {
  readonly desde: string;
  readonly hasta: string;
}

/**
 * De qué choferes se habla. El **propio** pertenece a una de las empresas del
 * grupo y se liquida por mes; el **eventual** trabaja para cualquier otra y se
 * consulta por un intervalo de fechas.
 */
export type TipoChofer = 'PROPIO' | 'EVENTUAL';

export interface RespuestaConsulta {
  readonly operacion: string;
  /** Las poblaciones consultadas: una o las dos. */
  readonly tiposChofer: readonly TipoChofer[];
  /**
   * Período de liquidación consultado, "AAAA-MM". Para los propios es el mes
   * que se eligió; para los eventuales, el mes en que **cierra** el intervalo.
   */
  readonly periodo: string;
  /** El intervalo de los eventuales. `null` si no se consultaron. */
  readonly intervalo: RangoDeFechas | null;
  /**
   * La fecha con la que se consultaron los conceptos de fecha manual, tal
   * como se pidió. `null` si no se cargó: esos conceptos quedan sin datos y
   * no se pueden tildar.
   */
  readonly rangoManual: RangoDeFechas | null;
  readonly tramos: readonly TramoTms[];
  /** Colaboradores distintos (por DNI) en el período. */
  readonly colaboradores: number;
  readonly novedades: readonly NovedadConsolidada[];
  readonly conceptos: readonly ResumenConcepto[];
  readonly advertencias: readonly string[];
}

/** Una novedad que el portal no puede mandar a Visma, con el motivo. */
export interface NovedadTrabada {
  readonly dni: string;
  readonly nombre: string;
  readonly concepto: string;
  readonly valor: number;
  readonly motivo: string;
}

/**
 * Una novedad lista para impactar, con el nombre del colaborador.
 *
 * Es el payload pero legible: la API no quiere el nombre y la pantalla no
 * puede mostrar solo un DNI.
 */
export interface ItemImpacto {
  readonly dni: string;
  /** Con qué documento entra a Visma. Distinto solo en la etapa de pruebas. */
  readonly dniVisma: string;
  readonly nombre: string;
  readonly legajo: string;
  /** "Propio" o "Eventual". */
  readonly tipoChofer: string;
  readonly concepto: string;
  readonly valor: number;
  readonly unidad: string;
  readonly desde: string | null;
  readonly hasta: string | null;
}

/** Una novedad que Visma recibió y rechazó. */
export interface NovedadRechazada {
  readonly dni: string;
  readonly concepto: string;
  readonly valor: number;
  readonly motivo: string;
}

/** Un impacto ya hecho: quién cargó qué período en Visma, y con qué resultado. */
export interface Impacto {
  readonly operacion: string;
  readonly periodo: string;
  readonly usuario: string;
  readonly impactadoEn: string;
  readonly novedades: number;
  readonly aceptadas: number;
  readonly rechazadas: number;
  readonly colaboradores: number;
  readonly conceptos: readonly string[];
}

/**
 * Respuesta de `/impactar`. La misma forma para el simulacro y para el
 * impacto real: `simulado` dice cuál de los dos fue.
 */
export interface RespuestaImpacto {
  readonly operacion: string;
  readonly periodo: string;
  readonly novedades: number;
  readonly colaboradores: number;
  readonly conceptos: readonly string[];
  readonly trabadas: readonly NovedadTrabada[];
  readonly advertencias: readonly string[];
  readonly simulado: boolean;
  /** Solo en el simulacro: las novedades, para elegir cuáles impactar. */
  readonly items?: readonly ItemImpacto[];
  /** Si el portal permite escribir a mano el DNI con el que se impacta. */
  readonly dniEditable?: boolean;
  /** Solo en el simulacro: el período ya impactado, si lo estaba. */
  readonly yaImpactado?: Impacto | null;
  /** Solo en el impacto real. */
  readonly aceptadas?: number;
  readonly rechazadas?: readonly NovedadRechazada[];
  readonly impactadoPor?: string;
}

// ===========================================================================
// Tipos espejo — configuración (back/src/configuracion/repositorioFeriados.ts)
// ===========================================================================

/**
 * Un feriado del calendario del portal.
 *
 * Los fijos valen todos los años y van sin año (Navidad es el 25/12 siempre);
 * los móviles —Carnaval, Semana Santa, los puentes— llevan el año en que caen.
 */
export type TipoFeriado = 'RECURRENTE' | 'PUNTUAL';

export interface Feriado {
  /** Identificador para la baja: "0-12-25" (todos los años) o "2026-4-3". */
  readonly clave: string;
  readonly tipo: TipoFeriado;
  /** Año del feriado móvil, o null si vale todos los años. */
  readonly anio: number | null;
  readonly mes: number;
  readonly dia: number;
  readonly descripcion: string;
  readonly creadoPor: string;
}

/**
 * Una regla del diccionario: qué acumula y a qué concepto de Visma va.
 *
 * `concepto` es lo único editable desde el portal; `editado` dice si el valor
 * viene de esa edición o del archivo de reglas.
 */
export interface ConceptoRegla {
  readonly id: string;
  readonly operacion: string;
  readonly nombre: string;
  /** Qué acumula la regla, en una línea y en lenguaje de negocio. */
  readonly detalle: string;
  readonly concepto: string;
  /** false = la regla no se calcula hasta que RRHH defina cómo se cuenta. */
  readonly activo: boolean;
  readonly editado: boolean;
  readonly actualizadoPor: string | null;
  /** `parameterId` que exige Visma. Es del concepto, no de la regla. */
  readonly parametro: number | null;
  readonly parametroEditado: boolean;
  /**
   * Si el concepto se consulta por un rango de fechas propio en lugar de por
   * el mes del período. Es del concepto, igual que el parámetro.
   */
  readonly rangoFecha: boolean;
  /** `true` cuando lo marcado acá pisa a lo que dice el archivo de reglas. */
  readonly rangoFechaEditado: boolean;
}
